import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { useDataStore } from "../../stores/useDataStore";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import dayjs from "dayjs";
import FormRow from "../FormLayout/FormLayout";
import { useNavigate } from "react-router-dom";

const AUTOCOMPLETE_SX = {
  "& .MuiOutlinedInput-root": {
    padding: "4px 32px 4px 14px !important",
    fontSize: 12,
  },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "0 !important",
    fontSize: 12,
  },
  "& .MuiAutocomplete-popupIndicator": { padding: "1px", marginRight: "-2px" },
  "& .MuiAutocomplete-clearIndicator": { padding: "1px" },
};

function RegulationNameJurisdictionRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Regulation Name
          </span>
          <Controller
            name="general.regulationName"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                multiline
                rows={3}
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Jurisdiction
          </span>
          <Controller
            name="general.jurisdiction"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                value={ctrl.value ?? ""}
                onChange={(e) => ctrl.onChange(e.target.value)}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function RequirementAppliedFromRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Applied From
          </span>
          <Controller
            name="admin.appliedFrom"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                name={ctrl.name}
                inputRef={ctrl.ref}
                size="small"
                margin="dense"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                value={ctrl.value ? dayjs(ctrl.value).format("YYYY-MM-DD") : ""}
                onChange={(e) => ctrl.onChange(e.target.value)}
              />
            )}
          />
        </div>
        <div></div>
      </div>
    </FormRow>
  );
}

function CitationAgencyRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Citation
          </span>
          <Controller
            name="general.citation"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Agency Name
          </span>
          <Controller
            name="general.agency"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function MediaLocationRow({ control }) {
  const locOptions = useLocations();

  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">Media</span>
          <Controller
            name="general.media"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                value={ctrl.value ?? ""}
                onChange={(e) => ctrl.onChange(e.target.value)}
                size="small"
                margin="dense"
                fullWidth
                select
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              >
                {(["Air", "Water", "Waste"] || []).map((o) => (
                  <MenuItem value={o} key={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Location
          </span>
          <Controller
            name="general.location"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={locOptions}
                value={(function () {
                  const v = ctrl.value;

                  if (!v) return null;

                  // Already a Location object
                  if (typeof v === "object") {
                    return locOptions.find((opt) => opt?.id === v?.id) || v;
                  }

                  // Backward compatibility for old string values
                  return locOptions.find((opt) => opt?.name === v) || null;
                })()}
                onChange={(_, v) => ctrl.onChange(v)}
                freeSolo={false}
                openOnFocus
                fullWidth
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option?.name || ""
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{ style: { maxHeight: 240 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

// load locations from backend for searchable dropdowns
function useLocations() {
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    let mounted = true;
    import("../../services/api").then((mod) => {
      mod
        .apiGet("/api/locations")
        .then((data) => {
          const list = Array.isArray(data) ? data : data.content || [];
          if (mounted)
            setLocations(
              (list || [])
                .map((l) => {
                  if (!l) return null;
                  // normalize to object with id and name
                  if (typeof l === "string") return { id: l, name: l };
                  const id = l.id || l._id || l.location || l.name;
                  const name = l.name || l.location || String(id || "");
                  return { id, name };
                })
                .filter(Boolean),
            );
        })
        .catch(() => {});
    });
    return () => (mounted = false);
  }, []);
  return locations;
}

// load task templates from backend for requirement task-template dropdown
function useTaskTemplates() {
  const [templates, setTemplates] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(50);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [query, setQuery] = useState("");
  const storeTemplates = useDataStore((s) => s.data)["My Task Templates"] || [];
  const storeTemplatesRef = React.useRef(storeTemplates);
  React.useEffect(() => {
    storeTemplatesRef.current = storeTemplates;
  }, [storeTemplates]);

  const fetchPage = React.useCallback(
    async (p = 0, q = "", append = false) => {
      if (fetchPage.__inFlight) return;
      fetchPage.__inFlight = true;
      setLoading(true);
      try {
        const mod = await import("../../services/api");
        const path = `/api/task-templates?page=${p}&size=${size}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
        const data = await mod.apiGet(path);
        const list = Array.isArray(data) ? data : data.content || [];
        const normalized = (list || [])
          .map((it) => {
            if (!it) return null;
            const id =
              it.id ||
              it._id ||
              it.templateId ||
              (typeof it === "string" ? it : undefined);
            const name =
              it.name ||
              it.templateName ||
              it.title ||
              it.template ||
              (typeof it === "string" ? it : "");
            return typeof it === "string"
              ? { id: it, name: it }
              : { ...it, id, name };
          })
          .filter(Boolean);
        setTemplates((prev) =>
          append ? [...prev, ...normalized] : normalized,
        );
        if (data && typeof data.totalElements !== "undefined") {
          const total = data.totalElements || 0;
          setHasMore((p + 1) * size < total);
        } else if (Array.isArray(data)) {
          setHasMore(normalized.length === size);
        } else if (data && data.content) {
          setHasMore((data.content || []).length === size);
        } else {
          setHasMore(false);
        }
      } catch (e) {
        if (!append) setTemplates(storeTemplatesRef.current || []);
        setHasMore(false);
      } finally {
        setLoading(false);
        fetchPage.__inFlight = false;
      }
    },
    [size],
  );

  useEffect(() => {
    let mounted = true;
    const id = setTimeout(() => {
      if (!mounted) return;
      setPage(0);
      fetchPage(0, query, false);
    }, 250);
    return () => {
      mounted = false;
      clearTimeout(id);
    };
  }, [query, fetchPage]);

  const loadMore = React.useCallback(async () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    await fetchPage(next, query, true);
  }, [loading, hasMore, page, query, fetchPage]);

  const reset = React.useCallback(() => {
    setTemplates([]);
    setPage(0);
    setHasMore(true);
    setQuery("");
  }, []);

  return { templates, loading, hasMore, loadMore, setQuery, reset };
}

// load requirements from backend for task requirement dropdown (paginated, infinite scroll)
function useRequirements() {
  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [size] = React.useState(50);
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const [query, setQuery] = React.useState("");

  const fetchPage = React.useCallback(
    async (p = 0, q = "", append = false) => {
      if (fetchPage.__inFlight) return;
      fetchPage.__inFlight = true;
      setLoading(true);
      try {
        const mod = await import("../../services/api");
        const path = `/api/requirements?page=${p}&size=${size}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
        const data = await mod.apiGet(path);
        const list = Array.isArray(data) ? data : data.content || [];
        const normalized = (list || [])
          .map((it) => {
            if (!it) return null;
            const id =
              it.id || it._id || (typeof it === "string" ? it : undefined);
            const name =
              it.title ||
              it.summary ||
              it.name ||
              (typeof it === "string" ? it : "");
            return { ...it, id, name };
          })
          .filter(Boolean);
        setItems((prev) => (append ? [...prev, ...normalized] : normalized));
        if (data && typeof data.totalElements !== "undefined") {
          const total = data.totalElements || 0;
          setHasMore((p + 1) * size < total);
        } else if (Array.isArray(data)) {
          setHasMore(normalized.length === size);
        } else {
          setHasMore(false);
        }
      } catch (e) {
        setHasMore(false);
      } finally {
        setLoading(false);
        fetchPage.__inFlight = false;
      }
    },
    [size],
  );

  React.useEffect(() => {
    let mounted = true;
    const id = setTimeout(() => {
      if (!mounted) return;
      fetchPage(0, query, false);
      setPage(0);
    }, 200);
    return () => {
      mounted = false;
      clearTimeout(id);
    };
  }, [query, fetchPage]);

  const loadMore = React.useCallback(async () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    await fetchPage(next, query, true);
  }, [loading, hasMore, page, query, fetchPage]);

  return { items, loading, hasMore, loadMore, setQuery };
}

function RequirementSelector({ control }) {
  const { items, loading, hasMore, loadMore, setQuery } = useRequirements();
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Requirement
          </span>
          <Controller
            name="details.requirement"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={items}
                loading={loading}
                filterOptions={(opts) => opts}
                getOptionLabel={(o) => (o ? o.name || o.summary || "" : "")}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return option === value;
                  return (
                    (option.id &&
                      value &&
                      value.id &&
                      option.id === value.id) ||
                    option.id === value ||
                    option.name === (value && (value.name || value)) ||
                    option.name === value ||
                    option === value
                  );
                }}
                value={(function () {
                  const v = ctrl.value;
                  if (!v) return null;
                  const id =
                    typeof v === "string" || typeof v === "number"
                      ? v
                      : v.id || v._id;
                  const match = (items || []).find(
                    (opt) =>
                      opt &&
                      (opt.id == id ||
                        opt._id == id ||
                        opt.name == v ||
                        opt === v),
                  );
                  return match || v || null;
                })()}
                onChange={(_, v) => {
                  if (!v) return ctrl.onChange(null);
                  const out = v.id
                    ? { id: v.id, name: v.name || v.summary }
                    : v;
                  ctrl.onChange(out);
                }}
                freeSolo={false}
                openOnFocus
                fullWidth
                onInputChange={(_, v) => setQuery(v)}
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{
                  style: { maxHeight: 240 },
                  onScroll: (e) => {
                    const node = e.currentTarget;
                    if (!node) return;
                    if (
                      node.scrollHeight - node.scrollTop - node.clientHeight <
                      80
                    ) {
                      if (hasMore && !loading) loadMore();
                    }
                  },
                }}
              />
            )}
          />
        </div>
        <div></div>
      </div>
    </FormRow>
  );
}

/* Requirement-specific paired rows */
function RequirementSummarySourceRow({ control, setValue }) {
  const storeRegs = useDataStore((s) => s.data)["My Regulations"] || [];
  const [localRegs, setLocalRegs] = React.useState(storeRegs || []);

  React.useEffect(() => {
    let mounted = true;
    async function fetchRegs() {
      try {
        const mod = await import("../../services/api");
        const resp = await mod.apiGet("/api/crs?page=0&size=1000");
        const list = Array.isArray(resp) ? resp : resp.content || [];
        if (mounted) setLocalRegs(list);
      } catch (e) {
        // fallback to storeRegs if API fails
        if (mounted && storeRegs && storeRegs.length) setLocalRegs(storeRegs);
      }
    }
    fetchRegs();
    return () => (mounted = false);
  }, [storeRegs]);

  // When regulations load in edit mode, populate the 4 derived admin fields from the selected regulation
  const currentSource = useWatch({ control, name: "general.source" });
  React.useEffect(() => {
    if (!currentSource || !localRegs.length || !setValue) return;
    const v = localRegs.find(
      (r) => (r.regulationName || r.name || r.title || "") === currentSource,
    );
    if (!v) return;
    setValue("admin.sourceCountry", v.country ? v.country.name || v.country.location : v.location || "");
    setValue("admin.sourceType", v.complianceType ? v.complianceType.type : "");
    setValue("admin.sourceRegion", v.managingRegion || "");
    setValue("admin.area", v.area || "");
    setValue("regulationProject.projectRegulationName", v.title || v.regulationName || v.name || "");
  }, [localRegs, currentSource]);

  const options = localRegs || [];
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-start gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Summary
          </span>
          <Controller
            name="general.summary"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                multiline
                rows={3}
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Source (Regulation)
          </span>
          <Controller
            name="general.source"
            control={control}
            render={({ field: ctrl }) => {
              const valueObj = options.find(
                (r) => (r.regulationName || r.name) === (ctrl.value || ""),
              );
              return (
                <>
                  <Autocomplete
                    sx={AUTOCOMPLETE_SX}
                    options={options}
                    getOptionLabel={(o) =>
                      o ? o.regulationName || o.name : ""
                    }
                    value={valueObj || null}
                    onChange={(_, v) => {
                      const nameVal = v ? v.regulationName || v.name : "";
                      ctrl.onChange(nameVal);
                      if (setValue && v) {
                        // populate admin fields from selected regulation
                        setValue(
                          "admin.sourceCountry",
                          v.country
                            ? v.country.name || v.country.location
                            : v.location || "",
                        );
                        setValue(
                          "admin.sourceType",
                          v.complianceType ? v.complianceType.type : "",
                        );
                        setValue("admin.sourceRegion", v.managingRegion || "");
                        setValue("admin.area", v.area || "");
                        // populate Regulation Name under Regulation Project
                        setValue(
                          "regulationProject.projectRegulationName",
                          v.title || v.regulationName || v.name || "",
                        );
                      }
                    }}
                    freeSolo={false}
                    openOnFocus
                    fullWidth
                    renderInput={(params) => (
                      <TextField {...params} size="small" margin="dense" />
                    )}
                    ListboxProps={{ style: { maxHeight: 240 } }}
                    autoHighlight
                  />
                </>
              );
            }}
          />
        </div>
      </div>
    </FormRow>
  );
}

function RequirementCitationExternalRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Citation / Reference
          </span>
          <Controller
            name="general.citation"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            External Source Link
          </span>
          <Controller
            name="general.externalLink"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function AdminStatusRecordRow({ control }) {
  const recordStatusValue = useWatch({ control, name: "admin.recordStatus" });
  return (
    <>
      <FormRow>
        <div className="flex gap-2 w-full">
          <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
            <span className="text-xs text-gray-600 whitespace-nowrap">
              Record Status
            </span>
            <Controller
              name="admin.recordStatus"
              control={control}
              render={({ field: ctrl }) => (
                <TextField
                  {...ctrl}
                  value={ctrl.value ?? ""}
                  onChange={(e) => ctrl.onChange(e.target.value)}
                  size="small"
                  margin="dense"
                  fullWidth
                  select
                  sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                >
                  {(["Draft", "Active", "Archived"] || []).map((o) => (
                    <MenuItem value={o} key={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>
          {/* radios moved to next line below */}
          <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
            <span className="text-xs text-gray-600 whitespace-nowrap">
              Record No
            </span>
            <Controller
              name="admin.recordNo"
              control={control}
              render={({ field: ctrl }) => (
                <TextField
                  {...ctrl}
                  size="small"
                  margin="dense"
                  fullWidth
                  sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                />
              )}
            />
          </div>
        </div>
      </FormRow>
      {(recordStatusValue || "")
        .toString()
        .toLowerCase()
        .startsWith("archiv") && (
        <FormRow>
          <div className="flex gap-2 w-full">
            <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
              <div className="text-xs text-gray-600">On Archive</div>
              <div>
                <Controller
                  name="admin.preserveRequirementTasks"
                  control={control}
                  render={({ field: ctrl }) => (
                    <RadioGroup
                      row
                      value={ctrl.value ? "preserve" : "cancel"}
                      onChange={(e) =>
                        ctrl.onChange(e.target.value === "preserve")
                      }
                    >
                      <FormControlLabel
                        value="preserve"
                        control={<Radio size="small" />}
                        label="Preserve Tasks"
                        sx={{
                          ".MuiFormControlLabel-label": {
                            fontSize: 13,
                            color: "#334155",
                          },
                          mr: 2,
                        }}
                      />
                      <FormControlLabel
                        value="cancel"
                        control={<Radio size="small" />}
                        label="Cancel Tasks"
                        sx={{
                          ".MuiFormControlLabel-label": {
                            fontSize: 13,
                            color: "#334155",
                          },
                        }}
                      />
                    </RadioGroup>
                  )}
                />
              </div>
              <div />
            </div>
          </div>
        </FormRow>
      )}
    </>
  );
}

function ReqAdminRecordRoaRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Record No
          </span>
          <Controller
            name="admin.recordNo"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            ROA Reference ID
          </span>
          <Controller
            name="admin.roaRefId"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqAdminStatusProtectedRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Record Status
          </span>
          <Controller
            name="admin.recordStatus"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                inputRef={ctrl.ref}
                value={ctrl.value ?? ""}
                onChange={(e) => ctrl.onChange(e.target.value)}
                size="small"
                margin="dense"
                select
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              >
                {(["Active", "Inactive"] || []).map((o) => (
                  <MenuItem value={o} key={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Protected
          </span>
          <Controller
            name="admin.protected"
            control={control}
            render={({ field: ctrl }) => (
              <div className="flex items-center">
                <Checkbox
                  size="small"
                  checked={!!ctrl.value}
                  onChange={(e) => ctrl.onChange(e.target.checked)}
                />
              </div>
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqAdminSourceTypeRegionRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Source Type
          </span>
          <Controller
            name="admin.sourceType"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                disabled
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Source Region
          </span>
          <Controller
            name="admin.sourceRegion"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                disabled
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqAdminCountryAreaRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Source Country
          </span>
          <Controller
            name="admin.sourceCountry"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                value={typeof ctrl.value === "object" ? ctrl.value?.name || "" : ctrl.value || ""}
                disabled
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">Area</span>
          <Controller
            name="admin.area"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                disabled
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqMethodStatusRecordRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Record Status
          </span>
          <Controller
            name="complianceMethod.methodRecordStatus"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                value={ctrl.value ?? ""}
                onChange={(e) => ctrl.onChange(e.target.value)}
                size="small"
                margin="dense"
                select
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              >
                {(["Active", "Inactive"] || []).map((o) => (
                  <MenuItem value={o} key={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Record
          </span>
          <Controller
            name="complianceMethod.methodRecord"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqMethodNameRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Compliance Method Name
          </span>
          <Controller
            name="complianceMethod.methodName"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1"></div>
      </div>
    </FormRow>
  );
}

function ReqMethodLocationTotalRow({ control }) {
  const locOptions = useLocations();
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Location
          </span>
          <Controller
            name="complianceMethod.methodLocation"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={locOptions}
                value={(function () {
                  const v = ctrl.value;
                  if (!v) return null;
                  if (typeof v === "object")
                    return locOptions.find((opt) => opt?.id === v?.id) || v;
                  return locOptions.find((opt) => opt?.name === v) || null;
                })()}
                onChange={(_, v) => ctrl.onChange(v)}
                freeSolo={false}
                openOnFocus
                fullWidth
                getOptionLabel={(option) =>
                  option ? option.name || String(option) : ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof option === "string" || typeof value === "string")
                    return String(option) === String(value);
                  return (
                    (option.id && value.id && option.id === value.id) ||
                    option.name === value.name
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{ style: { maxHeight: 240 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Total Tasks
          </span>
          <Controller
            name="complianceMethod.methodTotalTasks"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

/* Regulation Project paired rows for Requirement module */
function ReqProjectAndNameRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Project
          </span>
          <Controller
            name="regulationProject.project"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Project Name
          </span>
          <Controller
            name="regulationProject.projectName"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqProjectLeadRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Project Lead
          </span>
          <Controller
            name="regulationProject.projectLead"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function ReqProjectLocationStatusRow({ control }) {
  const locOptions = useLocations();
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Location
          </span>
          <Controller
            name="regulationProject.projectLocation"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={locOptions}
                value={(function () {
                  const v = ctrl.value;
                  if (!v) return null;
                  if (typeof v === "object")
                    return locOptions.find((opt) => opt?.id === v?.id) || v;
                  return locOptions.find((opt) => opt?.name === v) || null;
                })()}
                onChange={(_, v) => ctrl.onChange(v)}
                freeSolo={false}
                openOnFocus
                fullWidth
                getOptionLabel={(option) =>
                  option ? option.name || String(option) : ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof option === "string" || typeof value === "string")
                    return String(option) === String(value);
                  return (
                    (option.id && value.id && option.id === value.id) ||
                    option.name === value.name
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{ style: { maxHeight: 240 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Status
          </span>
          <Controller
            name="regulationProject.projectStatus"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function AdminCreatedRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Created By
          </span>
          <Controller
            name="admin.createdBy"
            control={control}
            render={({ field: ctrl }) => (
              <div className="text-[12px] text-gray-800 min-h-8 flex items-center">
                {formatDisplayValue(ctrl.value)}
              </div>
            )}
          />
        </div>
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Date Created
          </span>
          <Controller
            name="admin.dateCreated"
            control={control}
            render={({ field: ctrl }) => (
              <div className="text-[12px] text-gray-800 min-h-8 flex items-center">
                {formatDateValue(ctrl.value)}
              </div>
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function AdminModifiedRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Modified By
          </span>
          <Controller
            name="admin.modifiedBy"
            control={control}
            render={({ field: ctrl }) => (
              <div className="text-[12px] text-gray-800 min-h-8 flex items-center">
                {formatDisplayValue(ctrl.value)}
              </div>
            )}
          />
        </div>
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Date Modified
          </span>
          <Controller
            name="admin.dateModified"
            control={control}
            render={({ field: ctrl }) => (
              <div className="text-[12px] text-gray-800 min-h-8 flex items-center">
                {formatDateValue(ctrl.value)}
              </div>
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

// Helper to render display-friendly values (avoid rendering raw objects)
function formatDisplayValue(v) {
  if (v === null || typeof v === "undefined") return "-";
  if (typeof v === "string" || typeof v === "number") return v;
  if (typeof v === "object") {
    // user-like object
    if (v.firstName || v.lastName) {
      return (
        `${v.firstName || ""} ${v.lastName || ""}`.trim() ||
        v.username ||
        v.id ||
        "-"
      );
    }
    if (v.username) return v.username;
    if (v.name) return v.name;
    // fallback to JSON string (short)
    try {
      return JSON.stringify(v);
    } catch (e) {
      return "-";
    }
  }
  return String(v);
}

function formatDateValue(v) {
  if (!v) return "-";
  if (typeof v === "object") {
    // common date fields on objects
    if (v.date || v.createdOn || v.updatedOn) {
      const d = v.date || v.createdOn || v.updatedOn;
      return d ? dayjs(d).format("YYYY-MM-DD") : "-";
    }
    return "-";
  }
  try {
    return dayjs(v).isValid() ? dayjs(v).format("YYYY-MM-DD") : String(v);
  } catch (e) {
    return String(v);
  }
}

function AdminCountryStateRow({ control }) {
  const locOptions = useLocations();
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Country
          </span>
          <Controller
            name="admin.country"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={locOptions}
                value={(function () {
                  const v = ctrl.value;
                  if (!v) return null;
                  if (typeof v === "object")
                    return locOptions.find((opt) => opt?.id === v?.id) || v;
                  return locOptions.find((opt) => opt?.name === v) || null;
                })()}
                onChange={(_, v) => ctrl.onChange(v)}
                freeSolo={false}
                openOnFocus
                fullWidth
                getOptionLabel={(option) =>
                  option ? option.name || String(option) : ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof option === "string" || typeof value === "string")
                    return String(option) === String(value);
                  return (
                    (option.id && value.id && option.id === value.id) ||
                    option.name === value.name
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{ style: { maxHeight: 240 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            State/Province
          </span>
          <Controller
            name="admin.stateProvince"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={locOptions}
                value={(function () {
                  const v = ctrl.value;
                  if (!v) return null;
                  if (typeof v === "object")
                    return locOptions.find((opt) => opt?.id === v?.id) || v;
                  return locOptions.find((opt) => opt?.name === v) || null;
                })()}
                onChange={(_, v) => ctrl.onChange(v)}
                freeSolo={false}
                openOnFocus
                fullWidth
                getOptionLabel={(option) =>
                  option ? option.name || String(option) : ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  if (typeof option === "string" || typeof value === "string")
                    return String(option) === String(value);
                  return (
                    (option.id && value.id && option.id === value.id) ||
                    option.name === value.name
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{ style: { maxHeight: 240 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

function AdminRegionAreaRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Managing Region
          </span>
          <Controller
            name="admin.managingRegion"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">Area</span>
          <Controller
            name="admin.area"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              />
            )}
          />
        </div>
      </div>
    </FormRow>
  );
}

export default function DynamicForm({
  schema,
  defaultValues = {},
  onSubmit,
  onCancel,
  moduleName,
  itemIndex,
  submitRef,
  reviewOnly = false,
}) {
  const dataStore = useDataStore((s) => s.data);
  const createItem = useDataStore((s) => s.createItem);
  const updateItem = useDataStore((s) => s.updateItem);
  const { control, handleSubmit, reset, getValues, setValue } = useForm({
    defaultValues,
  });
  const watched = useWatch({ control });
  const saveTimer = React.useRef(null);
  const initialized = React.useRef(false);
  const [openSections, setOpenSections] = React.useState({});
  const locationOptions = useLocations();
  const navigate = useNavigate();
  const taskTemplate = useWatch({
    control,
    name: "general.taskTemplate",
  });

  const taskRecommended = useWatch({
    control,
    name: "general.taskRecommended",
  });

  const originalTaskTemplateId = useWatch({
    control,
    name: "_originalTaskTemplateId",
  });

  const originalTaskRecommended = useWatch({
    control,
    name: "_originalTaskRecommended",
  });

  const requiresRegenerationStrategy =
    (originalTaskTemplateId &&
      taskTemplate &&
      taskTemplate.id !== originalTaskTemplateId) ||
    (originalTaskRecommended === true &&
      (taskRecommended === "No" || taskRecommended === false));
  React.useEffect(() => {
    if (submitRef) submitRef.current = handleSubmit(onSubmit);
    return () => {
      if (submitRef) submitRef.current = null;
    };
  }, [handleSubmit, onSubmit, submitRef]);

  function isSectionOpen(name, defaultOpen = true) {
    return name in openSections ? openSections[name] : defaultOpen;
  }

  function toggleSection(name, defaultOpen = true) {
    setOpenSections((prev) => ({
      ...prev,
      [name]: !(name in prev ? prev[name] : defaultOpen),
    }));
  }

  function renderGroup(field) {
    const open = isSectionOpen(field.name, true);
    const isRegulationGeneralGroup =
      schema?.module === "Regulation" && field.name === "general";
    const isRegulationAdminGroup =
      schema?.module === "Regulation" && field.name === "admin";
    const isRequirementGeneralGroup =
      schema?.module === "Requirement" && field.name === "general";
    const isRequirementAdminGroup =
      schema?.module === "Requirement" && field.name === "admin";
    const isRequirementComplianceGroup =
      schema?.module === "Requirement" && field.name === "complianceMethod";
    const isRequirementProjectGroup =
      schema?.module === "Requirement" && field.name === "regulationProject";
    // If Task details group and creating new (no _id), render minimal fields only
    if (
      schema?.module === "Task" &&
      field.name === "details" &&
      !getValues()._id
    ) {
      return (
        <div className="pt-3">
          <div
            className="font-semibold text-xs mb-1 flex items-center gap-1 cursor-pointer select-none"
            onClick={() => toggleSection(field.name, true)}
          >
            <span>{field.label}</span>
            <span className="text-gray-500">▾</span>
          </div>
          <div className="border-t border-gray-200 pt-2 flex flex-col gap-1">
            <RequirementSelector control={control} />
            <div className="min-w-0">
              {renderField({
                name: `${field.name}.location`,
                label: "Location",
                type: "select",
                relation: "My Regulations",
                relationLabel: "location",
              })}
            </div>
            <div className="min-w-0">
              {renderField({
                name: `${field.name}.instruction`,
                label: "Instruction",
                type: "textarea",
              })}
            </div>
            <div className="min-w-0">
              {renderField({
                name: `${field.name}.type`,
                label: "Type",
                type: "select",
                options: [
                  "Self Assessment",
                  "Self Assessment Checklist",
                  "Inspection",
                  "Corrective Action",
                ],
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="pt-3">
        <div
          className="font-semibold text-xs mb-1 flex items-center gap-1 cursor-pointer select-none"
          onClick={() => toggleSection(field.name, true)}
        >
          <span>{field.label}</span>
          <span className="text-gray-500">{open ? "▾" : "▸"}</span>
        </div>
        {open && (
          <div className="border-t border-gray-200 pt-2 flex flex-col gap-1">
            {/* For Task details group, show a minimal create form when creating new (no _id) */}
            {schema?.module === "Task" &&
            field.name === "details" &&
            !getValues()._id ? (
              <>
                <RequirementSelector control={control} />
                {/* render location (reusing MediaLocationRow's location control) */}
                <div className="min-w-0">
                  {renderField({
                    name: `${field.name}.location`,
                    label: "Location",
                    type: "select",
                    relation: "My Regulations",
                    relationLabel: "location",
                  })}
                </div>
                <div className="min-w-0">
                  {renderField({
                    name: `${field.name}.instruction`,
                    label: "Instruction",
                    type: "textarea",
                  })}
                </div>
                <div className="min-w-0">
                  {renderField({
                    name: `${field.name}.attachment`,
                    label: "Attachment",
                    type: "file",
                  })}
                </div>
                <div className="min-w-0">
                  {renderField({
                    name: `${field.name}.type`,
                    label: "Type",
                    type: "select",
                    options: [
                      "Self Assessment",
                      "Self Assessment Checklist",
                      "Inspection",
                      "Corrective Action",
                    ],
                  })}
                </div>
              </>
            ) : (
              isRegulationGeneralGroup && (
                <>
                  <RegulationNameJurisdictionRow control={control} />
                  <CitationAgencyRow control={control} />
                  <MediaLocationRow control={control} />
                </>
              )
            )}
            {isRequirementGeneralGroup && (
              <>
                <RequirementSummarySourceRow
                  control={control}
                  setValue={setValue}
                />

                <RequirementCitationExternalRow control={control} />
              </>
            )}
            {isRegulationAdminGroup && (
              <>
                <AdminStatusRecordRow control={control} />
                <AdminCreatedRow control={control} />
                <AdminModifiedRow control={control} />
                <AdminCountryStateRow control={control} />
                <AdminRegionAreaRow control={control} />
              </>
            )}
            {isRequirementAdminGroup && (
              <>
                <ReqAdminRecordRoaRow control={control} />
                <ReqAdminStatusProtectedRow control={control} />
                <AdminCreatedRow control={control} />
                <AdminModifiedRow control={control} />
                <ReqAdminSourceTypeRegionRow control={control} />
                <ReqAdminCountryAreaRow control={control} />
              </>
            )}
            {isRequirementComplianceGroup && (
              <>
                <ReqMethodNameRow control={control} />
                <ReqMethodStatusRecordRow control={control} />
                <ReqMethodLocationTotalRow control={control} />
              </>
            )}
            {isRequirementProjectGroup && (
              <>
                <ReqProjectAndNameRow control={control} />
                <ReqProjectLeadRow control={control} />
                <ReqProjectLocationStatusRow control={control} />
              </>
            )}
            {field.fields
              .filter((sub) => {
                if (isRegulationGeneralGroup) {
                  if (
                    [
                      "regulationName",
                      "jurisdiction",
                      "agency",
                      "citation",
                      "media",
                      "location",
                    ].includes(sub.name)
                  ) {
                    return false;
                  }
                }

                // For requirement groups we render some paired rows above,
                // so exclude those individual fields from the generic mapping.
                if (isRequirementGeneralGroup) {
                  if (
                    ["summary", "source", "citation", "externalLink"].includes(
                      sub.name,
                    )
                  ) {
                    return false;
                  }
                }

                if (isRequirementAdminGroup) {
                  if (
                    [
                      "recordNo",
                      "roaRefId",
                      "recordStatus",
                      "protected",
                      "createdBy",
                      "dateCreated",
                      "modifiedBy",
                      "dateModified",
                      "appliedFrom",
                      "managingRegion",
                      "sourceType",
                      "sourceRegion",
                      "sourceCountry",
                      "area",
                    ].includes(sub.name)
                  ) {
                    return false;
                  }
                }

                if (isRequirementComplianceGroup) {
                  if (
                    [
                      "methodName",
                      "methodRecordStatus",
                      "methodRecord",
                      "methodLocation",
                      "methodTotalTasks",
                    ].includes(sub.name)
                  ) {
                    return false;
                  }
                }

                if (isRequirementProjectGroup) {
                  if (
                    [
                      "project",
                      "projectName",
                      "projectLead",
                      "projectLocation",
                      "projectStatus",
                    ].includes(sub.name)
                  ) {
                    return false;
                  }
                }

                if (isRegulationAdminGroup) {
                  if (
                    [
                      "recordStatus",
                      "recordNo",
                      "createdBy",
                      "dateCreated",
                      "modifiedBy",
                      "dateModified",
                      "country",
                      "stateProvince",
                      "managingRegion",
                      "area",
                    ].includes(sub.name)
                  ) {
                    return false;
                  }
                }

                return true;
              })
              .map((sub) => (
                <div key={sub.name} className="min-w-0">
                  {renderField({ ...sub, name: `${field.name}.${sub.name}` })}
                </div>
              ))}
            {isRequirementGeneralGroup && (
              <>
                <RequirementTaskTemplateRow control={control} />
                <RequirementAppliedFromRow control={control} />
                {requiresRegenerationStrategy && (
                  <RequirementTaskRegenerationRow control={control} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  function persistForm() {
    if (!moduleName) return;
    if (itemIndex === undefined || itemIndex === null) return;
    try {
      const vals = getValues();
      // Ensure we keep an _id if present
      const item = { ...(vals || {}), _id: vals?._id };
      // eslint-disable-next-line no-console
      // console.log("persistForm: saving", { moduleName, itemIndex, item });
      updateItem(moduleName, itemIndex, item);
    } catch (err) {
      // ignore
    }
  }

  React.useEffect(() => {
    // skip initial subscription trigger
    if (!moduleName) return;
    if (itemIndex === undefined || itemIndex === null) return;
    if (!initialized.current) {
      initialized.current = true;
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      persistForm();
    }, 250);

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [watched]);

  // Reset form whenever defaultValues change (e.g., editing an item)
  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  function renderField(field) {
    const name = field.name;
    const label = field.label;
    const type = field.type;
    const readOnlyKeys = [
      "projectRegulationName",
      "sourceCountry",
      "sourceType",
      "sourceRegion",
      "area",
    ];
    // existing read-only keys
    const readOnlyDisabled = readOnlyKeys.some(
      (k) => name === k || name.endsWith("." + k),
    );
    // when in reviewOnly mode, only allow editing of specific fields under details
    const reviewAllowedPrefixes = [
      "details.completionDetails",
      "details.dateCompleted",
      "details.status",
      "details.attachments",
    ];
    const isUnderDetails = name.startsWith("details.");
    const reviewAllowed = reviewAllowedPrefixes.some(
      (p) => name === p || name.startsWith(p + "."),
    );
    const shouldDisable =
      readOnlyDisabled ||
      (reviewOnly && isUnderDetails ? !reviewAllowed : reviewOnly);
    if (type === "group") return renderGroup(field);
    if (type === "list") {
      const isLocationDeterminations =
        schema?.module === "Regulation" &&
        (field.name === "locationApplicability.locationDeterminations" ||
          field.name.endsWith(".locationDeterminations"));

      if (isLocationDeterminations) {
        return (
          <LocationDeterminationsField
            key={field.name}
            field={field}
            control={control}
            isOpen={isSectionOpen(field.name, true)}
            onToggle={() => toggleSection(field.name, true)}
            persistForm={persistForm}
          />
        );
      }

      // Other lists use the generic ListField
      return (
        <ListField
          key={field.name}
          field={field}
          control={control}
          isOpen={isSectionOpen(field.name, true)}
          onToggle={() => toggleSection(field.name, true)}
          persistForm={persistForm}
          renderSubField={(sub, idx) =>
            renderField({
              ...sub,
              name: `${field.name}.${idx}.${sub.name}`,
            })
          }
        />
      );
    }

    const isSingleColumn =
      schema?.module === "Standard" || schema?.module === "Task";

    if (type === "text" || type === "url") {
      const isRegulationName =
        field.name === "regulationName" ||
        field.name.endsWith(".regulationName");

      if (isSingleColumn) {
        return (
          <div className="grid grid-cols-[160px_1fr] items-center gap-2 py-px">
            <span className="text-xs text-gray-600 whitespace-nowrap">
              {label}
            </span>
            <Controller
              name={name}
              control={control}
              render={({ field: ctrl }) => (
                <TextField
                  {...ctrl}
                  disabled={shouldDisable}
                  size="small"
                  margin="dense"
                  fullWidth
                  sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                />
              )}
            />
          </div>
        );
      }

      return (
        <FormRow>
          <div className="flex gap-2 w-full">
            <div className="flex-1 flex items-center gap-2">
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {label}
              </span>
              <Controller
                name={name}
                control={control}
                render={({ field: ctrl }) => (
                  <TextField
                    {...ctrl}
                    disabled={shouldDisable}
                    size="small"
                    margin="dense"
                    fullWidth
                    multiline={isRegulationName}
                    rows={isRegulationName ? 3 : 1}
                    sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                  />
                )}
              />
            </div>
            <div></div>
          </div>
        </FormRow>
      );
    }

    if (type === "textarea") {
      if (isSingleColumn) {
        return (
          <div className="grid grid-cols-[160px_1fr] items-start gap-2 py-px">
            <span className="text-xs text-gray-600 whitespace-nowrap pt-2">
              {label}
            </span>
            <Controller
              name={name}
              control={control}
              render={({ field: ctrl }) => (
                <TextField
                  {...ctrl}
                  disabled={shouldDisable}
                  size="small"
                  margin="dense"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                />
              )}
            />
          </div>
        );
      }

      return (
        <FormRow>
          <div className="flex gap-2 w-full">
            <div className="flex-1 flex items-center gap-2">
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {label}
              </span>
              <Controller
                name={name}
                control={control}
                render={({ field: ctrl }) => (
                  <TextField
                    {...ctrl}
                    disabled={shouldDisable}
                    size="small"
                    margin="dense"
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
                  />
                )}
              />
            </div>
            <div></div>
          </div>
        </FormRow>
      );
    }

    if (type === "select") {
      const staticOptions = field.options || [];
      let options = staticOptions;
      if (field.relation) {
        const related = dataStore[field.relation] || [];
        options = related.map(
          (r) =>
            r[field.relationLabel] ||
            r.regulationName ||
            r.standardName ||
            r.summary ||
            r.name,
        );
      }
      const isLocationField = !!(
        field.relation && field.relationLabel === "location"
      );

      const selectInput = (ctrl) =>
        isLocationField ? (
          <Autocomplete
            sx={AUTOCOMPLETE_SX}
            options={locationOptions.length ? locationOptions : options}
            getOptionLabel={(o) =>
              isLocationField ? (o ? o.name : "") : o || ""
            }
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return option === value;
              if (isLocationField) {
                return (
                  (option.id && value && value.id && option.id === value.id) ||
                  (option.id && value && option.id === value) ||
                  option.name === (value && value.name) ||
                  option.name === value ||
                  option === value
                );
              }
              return option === value;
            }}
            value={ctrl.value ?? null}
            onChange={(_, v) => ctrl.onChange(v)}
            disabled={shouldDisable}
            freeSolo={false}
            openOnFocus
            fullWidth
            renderInput={(params) => (
              <TextField {...params} size="small" margin="dense" />
            )}
            ListboxProps={{ style: { maxHeight: 240 } }}
          />
        ) : (
          <TextField
            {...ctrl}
            value={ctrl.value ?? ""}
            disabled={shouldDisable}
            onChange={(e) => ctrl.onChange(e.target.value)}
            size="small"
            margin="dense"
            select
            fullWidth
            sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
          >
            {(options || []).map((o) => (
              <MenuItem value={o} key={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
        );

      if (isSingleColumn) {
        return (
          <div className="grid grid-cols-[160px_1fr] items-center gap-2 py-px">
            <span className="text-xs text-gray-600 whitespace-nowrap">
              {label}
            </span>
            <Controller
              name={name}
              control={control}
              render={({ field: ctrl }) => selectInput(ctrl)}
            />
          </div>
        );
      }

      return (
        <FormRow>
          <div className="flex gap-2 w-full">
            <div className="flex-1 flex items-center gap-2">
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {label}
              </span>
              <Controller
                name={name}
                control={control}
                render={({ field: ctrl }) => selectInput(ctrl)}
              />
            </div>
            <div></div>
          </div>
        </FormRow>
      );
    }

    if (type === "date") {
      const dateInput = (ctrl) => (
        <TextField
          {...ctrl}
          name={ctrl.name}
          inputRef={ctrl.ref}
          disabled={shouldDisable}
          size="small"
          margin="dense"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
          value={ctrl.value ? dayjs(ctrl.value).format("YYYY-MM-DD") : ""}
          onChange={(e) => ctrl.onChange(e.target.value)}
        />
      );

      if (isSingleColumn) {
        return (
          <div className="grid grid-cols-[160px_1fr] items-center gap-2 py-px">
            <span className="text-xs text-gray-600 whitespace-nowrap">
              {label}
            </span>
            <Controller
              name={name}
              control={control}
              defaultValue=""
              render={({ field: ctrl }) => dateInput(ctrl)}
            />
          </div>
        );
      }

      return (
        <FormRow>
          <div className="flex gap-2 w-full">
            <div className="flex-1 flex items-center gap-2">
              <span className="text-xs text-gray-600 whitespace-nowrap">
                {label}
              </span>
              <Controller
                name={name}
                control={control}
                defaultValue=""
                render={({ field: ctrl }) => dateInput(ctrl)}
              />
            </div>
            <div></div>
          </div>
        </FormRow>
      );
    }

    if (type === "checkbox") {
      return (
        <Controller
          name={name}
          control={control}
          render={({ field: ctrl }) => (
            <FormRow label={label}>
              <div className="flex items-center">
                <Checkbox
                  size="small"
                  disabled={shouldDisable}
                  checked={!!ctrl.value}
                  onChange={(e) => ctrl.onChange(e.target.checked)}
                />
              </div>
            </FormRow>
          )}
        />
      );
    }

    if (type === "file") {
      return (
        <Controller
          name={name}
          control={control}
          render={({ field: ctrl }) => (
            <FormRow label={label}>
              <input
                className="text-sm"
                type="file"
                multiple
                disabled={shouldDisable}
                onChange={(e) => {
                  if (shouldDisable) return;
                  const files = Array.from(e.target.files || []);
                  if (!files.length) return;
                  const metas = files.map((f) => ({ file: f }));
                  ctrl.onChange(metas);
                }}
              />
            </FormRow>
          )}
        />
      );
    }

    return null;
  }

  const isSingleColumnModule =
    schema?.module === "Standard" || schema?.module === "Task";

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Render top-level group sections horizontally */}
      {(() => {
        const groups = (schema.fields || []).filter((f) => f.type === "group");
        const lists = (schema.fields || []).filter((f) => f.type === "list");
        const others = (schema.fields || []).filter(
          (f) => f.type !== "group" && f.type !== "list",
        );
        const content = (
          <div>
            {groups.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {groups.map((g) => (
                  <div
                    key={g.name}
                    style={{ flex: "1 1 320px", minWidth: 0, marginBottom: 12 }}
                  >
                    {renderGroup(g)}
                  </div>
                ))}
              </div>
            )}

            {others.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {others.map((f) => (
                  <div key={f.name} className="min-w-0">
                    {renderField(f)}
                  </div>
                ))}
              </div>
            )}

            {lists.length > 0 && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {lists.map((l) => {
                  const isRegulationRequirements =
                    schema?.module === "Regulation" &&
                    l.name === "requirements";
                  // Also render requirements for Standard module the same way
                  const isStandardRequirements =
                    schema?.module === "Standard" && l.name === "requirements";
                  if (isRegulationRequirements || isStandardRequirements) {
                    return (
                      <RequirementsListField
                        key={l.name}
                        field={l}
                        control={control}
                        isOpen={isSectionOpen(l.name, true)}
                        onToggle={() => toggleSection(l.name, true)}
                        persistForm={persistForm}
                      />
                    );
                  }

                  const isRequirementModule = schema?.module === "Requirement";

                  if (
                    isRequirementModule &&
                    l.name === "locationApplicabilities"
                  ) {
                    return (
                      <LocationApplicabilitiesField
                        key={l.name}
                        field={l}
                        control={control}
                        isOpen={isSectionOpen(l.name, true)}
                        onToggle={() => toggleSection(l.name, true)}
                        persistForm={persistForm}
                      />
                    );
                  }

                  if (isRequirementModule && l.name === "associatedAssets") {
                    return (
                      <AssociatedAssetsField
                        key={l.name}
                        field={l}
                        control={control}
                        isOpen={isSectionOpen(l.name, true)}
                        onToggle={() => toggleSection(l.name, true)}
                        persistForm={persistForm}
                      />
                    );
                  }

                  if (isRequirementModule && l.name === "privateAttachments") {
                    return (
                      <PrivateAttachmentsField
                        key={l.name}
                        field={l}
                        control={control}
                        isOpen={isSectionOpen(l.name, true)}
                        onToggle={() => toggleSection(l.name, true)}
                        persistForm={persistForm}
                      />
                    );
                  }

                  return (
                    <ListField
                      key={l.name}
                      field={l}
                      control={control}
                      isOpen={isSectionOpen(l.name, true)}
                      onToggle={() => toggleSection(l.name, true)}
                      renderSubField={(sub, idx) =>
                        renderField({
                          ...sub,
                          name: `${l.name}.${idx}.${sub.name}`,
                        })
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        );
        return isSingleColumnModule ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>{content}</div>
        ) : (
          content
        );
      })()}
      <div
        className="mt-2 flex gap-2"
        style={
          isSingleColumnModule
            ? { maxWidth: 560, margin: "8px auto 0 auto" }
            : {}
        }
      >
        <Button size="small" variant="contained" color="primary" type="submit">
          Save
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            reset();
            onCancel && onCancel();
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

function RequirementTaskTemplateRow({ control }) {
  const { templates, loading, hasMore, loadMore, setQuery } =
    useTaskTemplates();
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 grid grid-cols-[160px_1fr] items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Task Template
          </span>
          <Controller
            name="general.taskTemplate"
            control={control}
            render={({ field: ctrl }) => (
              <Autocomplete
                sx={AUTOCOMPLETE_SX}
                options={templates}
                loading={loading}
                filterOptions={(opts) => opts}
                getOptionLabel={(o) =>
                  o
                    ? typeof o === "string"
                      ? o
                      : o.name || o.templateName || ""
                    : ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return option === value;
                  return (
                    (option.id &&
                      value &&
                      value.id &&
                      option.id === value.id) ||
                    option.id === value ||
                    option.name === (value && (value.name || value)) ||
                    option.name === value ||
                    (typeof option === "string" && option === value)
                  );
                }}
                value={(function () {
                  const v = ctrl.value;
                  if (!v) return null;
                  const id =
                    typeof v === "string" || typeof v === "number"
                      ? v
                      : v.id || v._id;
                  const match = (templates || []).find(
                    (opt) =>
                      opt &&
                      (opt.id == id ||
                        opt._id == id ||
                        opt.name == v ||
                        opt === v),
                  );
                  return match || v || null;
                })()}
                onChange={(_, v) => {
                  // store simple object with id and name, or null
                  if (!v) return ctrl.onChange(null);
                  const out = v.id
                    ? { id: v.id, name: v.name || v.templateName }
                    : v;
                  ctrl.onChange(out);
                }}
                freeSolo={false}
                openOnFocus
                fullWidth
                onInputChange={(_, v) => setQuery(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    margin="dense"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <span style={{ marginRight: 8 }}>Loading...</span>
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  style: { maxHeight: 240 },
                  onScroll: (e) => {
                    const node = e.currentTarget;
                    if (!node) return;
                    if (
                      node.scrollHeight - node.scrollTop - node.clientHeight <
                      80
                    ) {
                      if (hasMore && !loading) loadMore();
                    }
                  },
                }}
              />
            )}
          />
        </div>
        <div></div>
      </div>
    </FormRow>
  );
}

function RequirementTaskRegenerationRow({ control }) {
  return (
    <FormRow>
      <div className="flex gap-2 w-full">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-xs text-gray-600 whitespace-nowrap">
            Task Regeneration Strategy
          </span>
          <Controller
            name="general.taskRegenerationStrategy"
            control={control}
            render={({ field: ctrl }) => (
              <TextField
                {...ctrl}
                size="small"
                margin="dense"
                select
                fullWidth
                sx={{ "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } }}
              >
                <MenuItem value="REGENERATE">REGENERATE</MenuItem>
                <MenuItem value="PRESERVE">PRESERVE</MenuItem>
              </TextField>
            )}
          />
        </div>
        <div></div>
      </div>
    </FormRow>
  );
}

function ListField({
  field,
  control,
  isOpen,
  onToggle,
  renderSubField,
  persistForm,
}) {
  const name = field.name;
  const { fields: items, append, remove } = useFieldArray({ control, name });

  return (
    <div className="pt-3">
      <div
        className="font-semibold text-xs mb-1 flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="flex items-center gap-1">
          <span>{field.label}</span>
          <span className="text-gray-500">{isOpen ? "▾" : "▸"}</span>
        </span>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            append({});
            setTimeout(() => persistForm && persistForm(), 0);
          }}
        >
          Add
        </Button>
      </div>
      {isOpen && (
        <div className="border-t border-gray-200 pt-2 flex flex-col gap-1">
          {items.map((it, idx) => (
            <div key={it.id} className="border rounded p-1">
              <div className="flex flex-col gap-1">
                {field.fields.map((sub) => (
                  <div key={sub.name} className="min-w-0">
                    {renderSubField(sub, idx)}
                  </div>
                ))}
              </div>
              <div className="mt-2">
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    remove(idx);
                    setTimeout(() => persistForm && persistForm(), 0);
                  }}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RequirementsListField({
  field,
  control,
  isOpen,
  onToggle,
  persistForm,
}) {
  const name = field.name;
  const { fields: items, append, remove } = useFieldArray({ control, name });
  const allData = useDataStore((s) => s.data);
  const allRequirements = allData["My Requirements"] || [];
  const navigate = useNavigate();
  // derive regulation name from the parent form values (watch) or fallback
  const _regulationNameA = useWatch({
    control,
    name: "general.regulationName",
  });
  const _regulationNameB = useWatch({ control, name: "general.name" });
  const regulationName = _regulationNameA || _regulationNameB || null;
  return (
    <div className="pt-3">
      <div
        className="font-semibold text-xs mb-1 flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="flex items-center gap-1">
          <span>{field.label}</span>
          <span className="text-gray-500">{isOpen ? "▾" : "▸"}</span>
        </span>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigate("/requirements/new");
          }}
        >
          Add
        </Button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 pt-2">
          <table className="w-full text-xs border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-1 text-left">Summary</th>
                <th className="p-1 text-left">Applies to</th>
                <th className="p-1 text-left">Citation</th>
                <th className="p-1 text-left">Record #</th>
                <th className="p-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t">
                  <td className="p-1">{it.title}</td>
                  <td className="p-1">{it.appliesTo}</td>
                  <td className="p-1">{it.citation}</td>
                  <td className="p-1">{it.recordNo}</td>
                  <td className="p-1">
                    {/* <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        remove(idx);
                        setTimeout(() => persistForm && persistForm(), 0);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                  <td className="p-1"> */}
                    <Button
                      size="small"
                      onClick={() => {
                        const row = it;
                        const defaultValues = {
                          _id: row.id || row._id || null,
                          _originalTaskTemplateId: row.taskTemplate?.id || null,
                          _originalTaskRecommended: row.taskRecommended,
                          general: {
                            summary: row.title || row.summary,
                            source: regulationName,
                            citation: row.citation,
                            externalLink: row.externalSourceLink,
                            details: row.details || row.description,
                            notes: row.additionalNotes,
                            appliesTo: row.appliesTo,
                            taskRecommended: row.taskRecommended ? "Yes" : "No",
                            taskTemplate: row.taskTemplate
                              ? row.taskTemplate.id
                                ? {
                                    id: row.taskTemplate.id,
                                    name: row.taskTemplate.name,
                                  }
                                : row.taskTemplate
                              : null,
                          },
                          admin: {
                            recordNo: row.recordNo,
                            roaRefId: row.roaReferenceId,
                            appliedFrom: row.appliedFrom || null,
                            recordStatus: row.recordStatus,
                            protected: !!row.protectedFlag,
                            sourceType: row.sourceType,
                            sourceRegion: row.sourceRegion,
                            sourceCountry: row.sourceCountry,
                            area: row.area,
                            createdBy:
                              row.createdBy &&
                              (row.createdBy.username || row.createdBy.name)
                                ? row.createdBy.username || row.createdBy.name
                                : undefined,
                            dateCreated: row.createdAt || undefined,
                            modifiedBy:
                              row.modifiedBy &&
                              (row.modifiedBy.username || row.modifiedBy.name)
                                ? row.modifiedBy.username || row.modifiedBy.name
                                : undefined,
                            dateModified: row.modifiedAt || undefined,
                          },
                          complianceMethod: {
                            methodName: row.complianceMethod || null,
                            methodRecordStatus:
                              row.complianceMethodRecordStatus || null,
                            methodRecord: row.complianceMethodRecord || null,
                            methodLocation:
                              row.complianceMethodLocation &&
                              (row.complianceMethodLocation.name ||
                                row.complianceMethodLocation.code ||
                                row.complianceMethodLocation.id),
                            methodTotalTasks:
                              row.complianceMethodTotalTasks || null,
                          },
                          locationApplicabilities:
                            row.requirementFacilities &&
                            Array.isArray(row.requirementFacilities)
                              ? row.requirementFacilities.map((rf) => ({
                                  id: rf.id || rf.id || null,
                                  location:
                                    rf.location &&
                                    (rf.location.id || rf.location.name)
                                      ? {
                                          id: rf.location.id,
                                          name: rf.location.name,
                                        }
                                      : rf.location,
                                  recordNo: rf.recordNo,
                                  applicable: rf.applicable,
                                  justification: rf.justification,
                                }))
                              : [],
                          associatedAssets:
                            row.associatedAssetsList &&
                            Array.isArray(row.associatedAssetsList)
                              ? row.associatedAssetsList.map((a) => ({
                                  id: a.id || a._id || null,
                                  commonName: a.assetName || null,
                                  assetType: a.assetType || null,
                                  assetLocation:
                                    a.location &&
                                    (a.location.name ||
                                      a.location.code ||
                                      a.location.id)
                                      ? {
                                          id: a.location.id,
                                          name: a.location.name,
                                        }
                                      : a.location,
                                  assetDateCreated: a.createdAt || null,
                                  assetRecordNo: a.recordNo || null,
                                  assetRecordStatus: a.recordStatus || null,
                                }))
                              : [],
                          privateAttachments:
                            row.attachmentsList &&
                            Array.isArray(row.attachmentsList)
                              ? row.attachmentsList.map((att) => ({
                                  id: att.id || att._id || null,
                                  attachmentName: att.filename || null,
                                  attachmentUrl: att.url || null,
                                }))
                              : [],
                          regulationProject: {
                            project: row.projectId || null,
                            projectName: row.projectName || null,
                            projectLead: row.projectLead || null,
                            projectLocation: row.projectLocation
                              ? row.projectLocation.name ||
                                row.projectLocation.code ||
                                row.projectLocation.id ||
                                null
                              : null,
                            projectStatus: row.projectStatus || null,
                            milestones: row.projectMilestones || null,
                            projectMedia: row.projectMedia || null,
                            projectRegulationName: regulationName,
                          },
                        };

                        navigate(`/requirements/new`, {
                          state: { defaultValues },
                        });
                      }}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="p-2 text-gray-500" colSpan={6}>
                    No requirements added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LocationDeterminationsField({
  field,
  control,
  isOpen,
  onToggle,
  persistForm,
}) {
  const name = field.name;
  const { fields: items, append, remove } = useFieldArray({ control, name });

  const [showForm, setShowForm] = React.useState(false);
  const [draft, setDraft] = React.useState({
    location: "",
    applicable: false,
    justification: "",
    recordNo: "",
  });

  const locOptions = useLocations();

  function handleChange(e) {
    const { name: n, value, type, checked } = e.target;
    setDraft((d) => ({ ...d, [n]: type === "checkbox" ? checked : value }));
  }

  function handleAdd() {
    if (!draft.location) return;
    append({ ...draft });
    setDraft({
      location: "",
      applicable: false,
      justification: "",
      recordNo: "",
    });
    setShowForm(false);
    setTimeout(() => persistForm && persistForm(), 0);
  }

  return (
    <div className="pt-3">
      <div
        className="font-semibold text-xs mb-1 flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="flex items-center gap-1">
          <span>{field.label}</span>
          <span className="text-gray-500">{isOpen ? "▾" : "▸"}</span>
        </span>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setShowForm(true);
          }}
        >
          Add
        </Button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 pt-2 text-xs">
          {showForm && (
            <div className="mb-2 border rounded p-2 bg-gray-50 flex flex-col gap-2">
              <div className="flex gap-2">
                <Autocomplete
                  sx={AUTOCOMPLETE_SX}
                  options={locOptions}
                  getOptionLabel={(o) =>
                    o ? o.name || o.locationName || o : ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return option === value;
                    return (
                      (option.id &&
                        value &&
                        value.id &&
                        option.id === value.id) ||
                      (option.id && value && option.id === value) ||
                      option.name === (value && value.name) ||
                      option.name === value ||
                      option === value
                    );
                  }}
                  value={draft.location || null}
                  onChange={(_, v) => setDraft((d) => ({ ...d, location: v }))}
                  freeSolo={false}
                  openOnFocus
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="location"
                      label="Location"
                      size="small"
                      margin="dense"
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: 240 } }}
                />
                <div className="flex items-center gap-1 mt-1">
                  <Checkbox
                    name="applicable"
                    size="small"
                    checked={draft.applicable}
                    onChange={handleChange}
                  />
                  <span>Applicable</span>
                </div>
              </div>
              <TextField
                name="justification"
                label="Justification"
                size="small"
                margin="dense"
                value={draft.justification}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="recordNo"
                label="Record No"
                size="small"
                margin="dense"
                value={draft.recordNo}
                onChange={handleChange}
                fullWidth
              />
              <div className="flex gap-2 justify-end mt-1">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleAdd}>
                  Add to Table
                </Button>
              </div>
            </div>
          )}

          <table className="w-full text-xs border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-1 text-left">Locaiton</th>
                <th className="p-1 text-left">Applicable</th>
                <th className="p-1 text-left">Justification</th>
                <th className="p-1 text-left">Record No.</th>
                <th className="p-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t">
                  <td className="p-1">
                    {it.location ? it.location.name || it.location : ""}
                  </td>
                  <td className="p-1">{it.applicable ? "Yes" : "No"}</td>
                  <td className="p-1">{it.justification}</td>
                  <td className="p-1">{it.recordNo}</td>
                  <td className="p-1">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        remove(idx);
                        setTimeout(() => persistForm && persistForm(), 0);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="p-2 text-gray-500" colSpan={5}>
                    No Location facility added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LocationApplicabilitiesField({
  field,
  control,
  isOpen,
  onToggle,
  persistForm,
}) {
  const name = field.name;
  const { fields: items, append, remove } = useFieldArray({ control, name });
  const [showForm, setShowForm] = React.useState(false);
  const [draft, setDraft] = React.useState({
    location: "",
    recordNo: "",
    applicable: false,
    justification: "",
  });

  const locOptions = useLocations();

  function handleChange(e) {
    const { name: n, value, type, checked } = e.target;
    setDraft((d) => ({ ...d, [n]: type === "checkbox" ? checked : value }));
  }

  function handleAdd() {
    if (!draft.location) return;
    append({ ...draft });
    setDraft({
      location: "",
      recordNo: "",
      applicable: false,
      justification: "",
    });
    setShowForm(false);
    setTimeout(() => persistForm && persistForm(), 0);
  }
  return (
    <div className="pt-3">
      <div
        className="font-semibold text-xs mb-1 flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="flex items-center gap-1">
          <span>{field.label}</span>
          <span className="text-gray-500">{isOpen ? "▾" : "▸"}</span>
        </span>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setShowForm(true);
          }}
        >
          Add
        </Button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 pt-2 text-xs">
          {showForm && (
            <div className="mb-2 border rounded p-2 bg-gray-50 flex flex-col gap-2">
              <div className="flex gap-2">
                <Autocomplete
                  sx={AUTOCOMPLETE_SX}
                  options={locOptions}
                  getOptionLabel={(o) =>
                    o ? o.name || o.locationName || o : ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return option === value;
                    return (
                      (option.id &&
                        value &&
                        value.id &&
                        option.id === value.id) ||
                      (option.id && value && option.id === value) ||
                      option.name === (value && value.name) ||
                      option.name === value ||
                      option === value
                    );
                  }}
                  value={draft.location || null}
                  onChange={(_, v) => setDraft((d) => ({ ...d, location: v }))}
                  freeSolo={false}
                  openOnFocus
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="location"
                      label="Location"
                      size="small"
                      margin="dense"
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: 240 } }}
                />
                <TextField
                  name="recordNo"
                  label="Record No."
                  size="small"
                  margin="dense"
                  value={draft.recordNo}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <div className="flex gap-2 items-center">
                <Checkbox
                  name="applicable"
                  size="small"
                  checked={draft.applicable}
                  onChange={handleChange}
                />
                <span>Applicable</span>
              </div>
              <TextField
                name="justification"
                label="Justification"
                size="small"
                margin="dense"
                value={draft.justification}
                onChange={handleChange}
                fullWidth
              />
              <div className="flex gap-2 justify-end mt-1">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleAdd}>
                  Add to Table
                </Button>
              </div>
            </div>
          )}

          <table className="w-full text-xs border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-1 text-left">Location</th>
                <th className="p-1 text-left">Record No.</th>
                <th className="p-1 text-left">Applicable</th>
                <th className="p-1 text-left">Justification</th>
                <th className="p-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t">
                  <td className="p-1">
                    {it.location ? it.location.name || it.location : ""}
                  </td>
                  <td className="p-1">{it.recordNo}</td>
                  <td className="p-1">{it.applicable ? "Yes" : "No"}</td>
                  <td className="p-1">{it.justification}</td>
                  <td className="p-1">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        remove(idx);
                        setTimeout(() => persistForm && persistForm(), 0);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="p-2 text-gray-500" colSpan={5}>
                    No Location applicabilities added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AssociatedAssetsField({
  field,
  control,
  isOpen,
  onToggle,
  persistForm,
}) {
  const name = field.name;
  const { fields: items, append, remove } = useFieldArray({ control, name });
  const [showForm, setShowForm] = React.useState(false);
  const [draft, setDraft] = React.useState({
    commonName: "",
    assetType: "",
    assetLocation: "",
    assetDateCreated: "",
    assetRecordNo: "",
    assetRecordStatus: "",
  });

  const locOptions = useLocations();

  function handleChange(e) {
    const { name: n, value } = e.target;
    setDraft((d) => ({ ...d, [n]: value }));
  }

  function handleAdd() {
    if (!draft.commonName) return;
    append({ ...draft });
    setDraft({
      commonName: "",
      assetType: "",
      assetLocation: "",
      assetDateCreated: "",
      assetRecordNo: "",
      assetRecordStatus: "",
    });
    setShowForm(false);
    setTimeout(() => persistForm && persistForm(), 0);
  }

  return (
    <div className="pt-3">
      <div
        className="font-semibold text-xs mb-1 flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="flex items-center gap-1">
          <span>{field.label}</span>
          <span className="text-gray-500">{isOpen ? "▾" : "▸"}</span>
        </span>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setShowForm(true);
          }}
        >
          Add
        </Button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 pt-2 text-xs">
          {showForm && (
            <div className="mb-2 border rounded p-2 bg-gray-50 flex flex-col gap-2">
              <div className="flex gap-2">
                <TextField
                  name="commonName"
                  label="Common Name"
                  size="small"
                  margin="dense"
                  value={draft.commonName}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  name="assetType"
                  label="Asset Type"
                  size="small"
                  margin="dense"
                  value={draft.assetType}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <div className="flex gap-2">
                <Autocomplete
                  sx={AUTOCOMPLETE_SX}
                  options={locOptions}
                  value={draft.assetLocation || null}
                  onChange={(_, v) =>
                    setDraft((d) => ({ ...d, assetLocation: v }))
                  }
                  getOptionLabel={(option) =>
                    option ? option.name || String(option) : ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    if (typeof option === "string" || typeof value === "string")
                      return String(option) === String(value);
                    return option.id === value.id || option.name === value.name;
                  }}
                  freeSolo={false}
                  openOnFocus
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="assetLocation"
                      label="Location"
                      size="small"
                      margin="dense"
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: 240 } }}
                />
                <TextField
                  name="assetDateCreated"
                  label="Date Created"
                  size="small"
                  margin="dense"
                  value={draft.assetDateCreated}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <div className="flex gap-2">
                <TextField
                  name="assetRecordNo"
                  label="Record No."
                  size="small"
                  margin="dense"
                  value={draft.assetRecordNo}
                  onChange={handleChange}
                  fullWidth
                />
                <TextField
                  name="assetRecordStatus"
                  label="Record Status"
                  size="small"
                  margin="dense"
                  value={draft.assetRecordStatus}
                  onChange={handleChange}
                  fullWidth
                />
              </div>
              <div className="flex gap-2 justify-end mt-1">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleAdd}>
                  Add to Table
                </Button>
              </div>
            </div>
          )}

          <table className="w-full text-xs border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-1 text-left">Common Name</th>
                <th className="p-1 text-left">Asset Type</th>
                <th className="p-1 text-left">Location</th>
                <th className="p-1 text-left">Date Created</th>
                <th className="p-1 text-left">Record No.</th>
                <th className="p-1 text-left">Record Status</th>
                <th className="p-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t">
                  <td className="p-1">{it.commonName}</td>
                  <td className="p-1">{it.assetType}</td>
                  <td className="p-1">
                    {it.assetLocation
                      ? it.assetLocation.name || it.assetLocation
                      : ""}
                  </td>
                  <td className="p-1">{it.assetDateCreated}</td>
                  <td className="p-1">{it.assetRecordNo}</td>
                  <td className="p-1">{it.assetRecordStatus}</td>
                  <td className="p-1">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        remove(idx);
                        setTimeout(() => persistForm && persistForm(), 0);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="p-2 text-gray-500" colSpan={7}>
                    No associated assets added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PrivateAttachmentsField({
  field,
  control,
  isOpen,
  onToggle,
  persistForm,
}) {
  const name = field.name;
  const { fields: items, append, remove } = useFieldArray({ control, name });
  const [showForm, setShowForm] = React.useState(false);
  const [draft, setDraft] = React.useState({
    attachmentName: "",
    attachmentUrl: "",
  });

  function handleChange(e) {
    const { name: n, value } = e.target;
    setDraft((d) => ({ ...d, [n]: value }));
  }

  function handleAdd() {
    if (!draft.attachmentName) return;
    append({ ...draft });
    setDraft({ attachmentName: "", attachmentUrl: "" });
    setShowForm(false);
    setTimeout(() => persistForm && persistForm(), 0);
  }

  return (
    <div className="pt-3">
      <div
        className="font-semibold text-xs mb-1 flex items-center justify-between cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="flex items-center gap-1">
          <span>{field.label}</span>
          <span className="text-gray-500">{isOpen ? "▾" : "▸"}</span>
        </span>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setShowForm(true);
          }}
        >
          Add
        </Button>
      </div>

      {isOpen && (
        <div className="border-t border-gray-200 pt-2 text-xs">
          {showForm && (
            <div className="mb-2 border rounded p-2 bg-gray-50 flex flex-col gap-2">
              <TextField
                name="attachmentName"
                label="Attachment Name"
                size="small"
                margin="dense"
                value={draft.attachmentName}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="attachmentUrl"
                label="URL"
                size="small"
                margin="dense"
                value={draft.attachmentUrl}
                onChange={handleChange}
                fullWidth
              />
              <div className="flex gap-2 justify-end mt-1">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button size="small" variant="contained" onClick={handleAdd}>
                  Add to Table
                </Button>
              </div>
            </div>
          )}

          <table className="w-full text-xs border">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-1 text-left">Attachment Name</th>
                <th className="p-1 text-left">URL</th>
                <th className="p-1 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.id} className="border-t">
                  <td className="p-1">{it.attachmentName}</td>
                  <td className="p-1">{it.attachmentUrl}</td>
                  <td className="p-1">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        remove(idx);
                        setTimeout(() => persistForm && persistForm(), 0);
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td className="p-2 text-gray-500" colSpan={3}>
                    No private document attachments added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
